import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { useThemeAsset } from '@shared/configs';
import { ChangeTheme } from '@features/change-theme';
import EFL_TEXT from '@shared/assets/pictures/efl_text_font.png';
import WHITE_EFL_TEXT from '@shared/assets/pictures/efl-text-white.png';
import HALF_LOGO from '@shared/assets/pictures/eclipse_fitness_lv.png';

import {
  Button,
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  buttonVariants,
} from '@shared/ui';
import { useAuth } from '@entities/auth';

interface RouteProps {
  href: string;
  label: string;
}

const routeList: RouteProps[] = [
  {
    href: '#home',
    label: 'Home',
  },
  {
    href: '#features',
    label: 'Features',
  },
  {
    href: '#services',
    label: 'Services',
  },
  {
    href: '#pricing',
    label: 'Pricing',
  },
  {
    href: 'login',
    label: 'Login',
  },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { user, logout } = useAuth();
  const THEME_LOGO = useThemeAsset(EFL_TEXT, WHITE_EFL_TEXT);

  return (
    <header className="sticky top-0 z-40 w-full border-b-[1px] bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <NavigationMenu className="mx-auto">
        <NavigationMenuList className="container flex h-14 w-screen justify-between px-4 ">
          <NavigationMenuItem className="flex font-bold">
            {/* <EclipseIcon /> */}
            <Link
              rel="noreferrer noopener"
              to="/"
              className="flex items-center justify-between gap-2"
            >
              <img src={HALF_LOGO} alt="" width={45} />
              <img src={THEME_LOGO} height={55} width={175} />
            </Link>
          </NavigationMenuItem>

          {/* mobile */}
          <span className="flex md:hidden">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger className="px-2">
                <div>
                  <Menu
                    className="flex h-5 w-5 md:hidden"
                    onClick={() => setIsOpen(true)}
                  >
                    <span className="sr-only">Menu Icon</span>
                  </Menu>
                </div>
              </SheetTrigger>

              <SheetContent side={'left'}>
                <SheetHeader>
                  <SheetTitle className="flex cursor-pointer items-center justify-center gap-2">
                    <img src={HALF_LOGO} alt="" width={45} />
                    <img src={THEME_LOGO} height={55} width={175} />
                  </SheetTitle>
                </SheetHeader>
                <nav className="mt-4 flex flex-col items-center justify-center gap-2">
                  {routeList.map(({ href, label }: RouteProps) =>
                    user && label === 'Login' ? (
                      <Button key={label} onClick={logout} size={'sm'}>
                        {'Logout'}
                      </Button>
                    ) : (
                      <Link
                        rel="noreferrer noopener"
                        to={href}
                        onClick={() => setIsOpen(false)}
                        key={label}
                        className={buttonVariants({
                          variant:
                            label === 'Login' && user !== null
                              ? 'destructive'
                              : label === 'Login' && user === null
                                ? 'outline'
                                : 'ghost',
                        })}
                      >
                        {label}
                      </Link>
                    )
                  )}
                </nav>
              </SheetContent>
            </Sheet>
            {/*THEME */}
            <ChangeTheme />
          </span>

          {/* desktop */}
          <nav className="hidden items-center gap-2 md:flex">
            {routeList.map((route: RouteProps, i) =>
              user && route.label === 'Login' ? (
                <Button key={route.label} onClick={logout} size={'sm'}>
                  {'Logout'}
                </Button>
              ) : (
                <a
                  rel="noreferrer noopener"
                  href={route.href}
                  key={i}
                  className={`text-[17px] ${buttonVariants({
                    variant: route.label === 'Login' ? 'ghost' : 'ghost',
                  })}`}
                >
                  {route.label}
                </a>
              )
            )}
            <div className="flex items-center p-2">
              {/*THEME */}
              <ChangeTheme />
            </div>
          </nav>
        </NavigationMenuList>
      </NavigationMenu>
    </header>
  );
};
